// import { track } from 'mixpanel-browser';
import {
  SortableTable,
  SortableTableProps,
} from '@aibees/elephant/components/sortable-table';
import { SortableItemRow } from '@aibees/elephant/components/sortable-table/components/sortable-item-row';
import { useSearchTypeFromRoute, useTrackEvents } from '@aibees/elephant/hooks';
import { ElephantPresetType } from '@aibees/elephant/types';
import {
  useCreatePresetMutationApi,
  useUpdatePresetMutationApi,
} from '@aibees/shared/api';
import { classnames } from '@aibees/shared/utils';
import { Stack } from '@mui/material';
import { memo, useEffect } from 'react';
import { useBuyingTeamDialogsContext } from '../../context';
import { useBrowseBuyingTeamsApi } from '../../hooks/use-browse-buying-teams-api';
import { BuyingTeamSettingsDialogHeader } from './components/buying-team-settings-dialog-header';
import { columns } from './config';
import * as Styled from './styles';
import * as SX from './sx';
import type { BuyingTeamSettingsDialogProps as Props } from './types';

export const BuyingTeamSettingsDialog = memo(
  ({ className, isBuyingTeamView }: Props) => {
    const searchType = useSearchTypeFromRoute();
    const { track } = useTrackEvents();
    const { createDialogState, settingsDialogState } =
      useBuyingTeamDialogsContext();
    const { matrixBrowseApi, orderBrowseApi } = useBrowseBuyingTeamsApi();

    const orderPreset = orderBrowseApi.data?.content.at(0);
    const orderCreateApi = useCreatePresetMutationApi<string[]>(true);
    const orderUpdateApi = useUpdatePresetMutationApi<string[]>(
      orderPreset?.id ?? ''
    );

    const onChange: SortableTableProps['onChange'] = (items) => {
      const newOrder = items.map((x) => x.id);
      if (!orderPreset) {
        orderCreateApi
          .mutateAsync({
            name: ElephantPresetType.BUYING_TEAM_MATRIX_ORDER,
            type: ElephantPresetType.BUYING_TEAM_MATRIX_ORDER,
            value: newOrder ?? '[]',
          })
          .then(() => {
            return [orderBrowseApi.refetch(), matrixBrowseApi.refetch()];
          });
      } else {
        const newPreset = {
          ...orderPreset,
          ...{ value: newOrder ?? '[]' },
        };
        orderUpdateApi
          .mutateAsync(newPreset)
          .then(() => [orderBrowseApi.refetch(), matrixBrowseApi.refetch()]);
      }
    };

    useEffect(() => {
      if (settingsDialogState?.value) {
        track('VISIT_BUYING_TEAM_DIALOG', { searchType });
      }
    }, [settingsDialogState?.value]);

    return !isBuyingTeamView ? (
      <Styled.Wrapper
        className={classnames('BuyingTeamSettingsDialog', className)}
        state={settingsDialogState}
        sx={SX.root}
        contentSx={{ p: 0 }}
      >
        <BuyingTeamSettingsDialogHeader />
        <Stack sx={SX.contentContainer}>
          <Styled.Content>
            <Styled.SortableContent>
              <SortableTable
                items={matrixBrowseApi.data?.content ?? []}
                columns={columns}
                loading={orderBrowseApi.isLoading || matrixBrowseApi.isLoading}
                renderItem={(a) => (
                  <SortableItemRow
                    item={a}
                    createDialogState={createDialogState}
                    openingDialogState={settingsDialogState}
                    columns={columns}
                  />
                )}
                onChange={onChange}
              />
            </Styled.SortableContent>
          </Styled.Content>
        </Stack>
      </Styled.Wrapper>
    ) : (
      <Stack sx={SX.contentContainer}>
        <Styled.Content>
          <Styled.SortableContent>
            <SortableTable
              items={matrixBrowseApi.data?.content ?? []}
              columns={columns}
              loading={orderBrowseApi.isLoading || matrixBrowseApi.isLoading}
              renderItem={(a) => (
                <SortableItemRow
                  item={a}
                  createDialogState={createDialogState}
                  openingDialogState={settingsDialogState}
                  columns={columns}
                />
              )}
              onChange={onChange}
            />
          </Styled.SortableContent>
        </Styled.Content>
      </Stack>
    );
  }
);

BuyingTeamSettingsDialog.displayName = 'BuyingTeamSettingsDialog';
